import React, { useEffect, useState } from 'react';
import { FaTimes, FaGift, FaCheck, FaSearch } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useVouchers } from '../hooks/useVouchers';
import { modalVariants } from '../animations';

const VouchersModal = ({ isOpen, onClose }) => {
  const { vouchers, loading, fetchVouchers, acceptVoucher } = useVouchers();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchVouchers();
    }
  }, [isOpen, fetchVouchers]);

  if (!isOpen) return null;

  const filteredVouchers = vouchers
    .filter(voucher =>
      voucher.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      voucher.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Les bons non acceptés (is_used = false) en premier
      if (a.is_used !== b.is_used) {
        return a.is_used ? 1 : -1;
      }
      // Ensuite, trier par date de création (plus récent en premier)
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const handleAccept = async (voucherId) => {
    const success = await acceptVoucher(voucherId);
    if (!success) {
      alert('Erreur lors de l\'acceptation du bon');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div {...modalVariants.overlay} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 md:p-4">
          <motion.div {...modalVariants.content} className="bg-gray-50 rounded-xl md:rounded-2xl w-full md:w-[98%] h-full md:h-auto md:max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 bg-white flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Mes bons d'achats</h2>
            <p className="text-sm text-gray-500 mt-1">{filteredVouchers.length} bon{filteredVouchers.length > 1 ? 's' : ''} affiché{filteredVouchers.length > 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Recherche */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fideliz-blue w-64"
              />
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fideliz-blue"></div>
            </div>
          ) : vouchers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaGift className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">Aucun bon d'achat</p>
              <p className="text-xs text-gray-500">Vos demandes de retraits apparaîtront ici</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bénéficiaire</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date demande</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Montant</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredVouchers.map(voucher => (
                    <tr key={voucher.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900">
                          {voucher.first_name} {voucher.last_name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {new Date(voucher.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-gray-900">
                          {voucher.value.toLocaleString('fr-FR')} €
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end">
                          {voucher.is_used ? (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                              Utilisé le {new Date(voucher.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                            </span>
                          ) : (
                            <button
                              onClick={() => handleAccept(voucher.id)}
                              className="px-4 py-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                            >
                              <FaCheck className="w-4 h-4" />
                              Accepter
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VouchersModal;

